import React, { useContext } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { useSelector, useDispatch } from 'react-redux'
import { AmplitudeContext } from '@homebot/shared-util-quartermaster'

// Actions
import { sendRequestForDirectMessage } from '../../../../actions/homeowner'

// Constants
import { REVERSE_MORTGAGE_TOPIC_KEY } from '../../../../constants/topicKeys'
import { SYSTEM_REQUEST, QUESTIONS } from '../../../../constants/messages'

// Components
import { Page, If, List, Text, Unless, OutreachButton } from '../../..'
import { OverlayQuestionsFooter } from '../../../shared/Routing'
import { Player } from '../../../shared/Player'

// Selectors
import {
  selectDmFailure,
  selectDmRequest,
  selectDmSuccess,
} from '../../../../store/selectors/homeowner'
import {
  selectReverseMortgageVideoEnabled,
  selectReverseMortgageVideoExternalId,
  selectReverseMortgageVideoType,
} from '../../../../store/selectors/customerProfile'

// Assets
import reverseMortgageGif from './gifs/reverse-mortgage.gif'
import LoanSvg from './svgs/LoanSvg'
import HelocSvg from './svgs/HelocSvg'
import DownsizingSvg from './svgs/DownsizingSvg'

// Styles
import styles from './ReverseMortgagePanel.module.scss'

const questionMessages = defineMessages({
  retirementCashFlow: {
    id: 'Questions.reverseMortgages.retirementCashFlow',
    defaultMessage: 'Can you help me plan my retirement cash flow?',
  },
})

const buttonMessages = defineMessages({
  buttonText: {
    id: 'ReverseMortgage.requestMoreButton.initial',
    defaultMessage: 'Request more details',
  },
  buttonSuccess: {
    id: 'ReverseMortgage.requestMoreButton.success',
    defaultMessage: 'Message sent!',
  },
  buttonFailure: {
    id: 'ReverseMortgage.requestMoreButton.failure',
    defaultMessage: 'Please try again later!',
  },
})

const ReverseMortgagePanel = () => {
  const videoEnabled = useSelector(selectReverseMortgageVideoEnabled)
  const videoId = useSelector(selectReverseMortgageVideoExternalId)
  const videoType = useSelector(selectReverseMortgageVideoType)
  const dispatch = useDispatch()
  const dmRequest = useSelector(selectDmRequest)
  const dmSuccess = useSelector(selectDmSuccess)
  const dmFailure = useSelector(selectDmFailure)
  const intl = useIntl()
  const { ampli } = useContext(AmplitudeContext)

  const handleClick = () => {
    const message = intl.formatMessage(SYSTEM_REQUEST.reverseMortgageMessage)
    const topic = intl.formatMessage(SYSTEM_REQUEST.reverseMortgageTopic)
    dispatch(
      sendRequestForDirectMessage(
        message,
        topic,
        REVERSE_MORTGAGE_TOPIC_KEY,
        ampli
      )
    )
  }

  return (
    <Page>
      <Page.Body data-qa='reverseMortgagePanel'>
        <Text
          className={styles.panelTitle}
          size='xl'
          variant='neutralCool'
          variantCount='900'
        >
          <FormattedMessage
            id='reverseMortgage.panelTitle'
            defaultMessage='A reverse mortgage might help you better manage income during retirement years'
          />
        </Text>
        <div>
          <If condition={videoEnabled}>
            <Player
              videoType={videoType}
              videoId={videoId}
              className={styles.videoPlayer}
            />
          </If>
          <Unless condition={videoEnabled}>
            <img
              src={reverseMortgageGif}
              alt='piggy bank!'
              className={styles.reverseMortgageImage}
            />
          </Unless>
        </div>

        <Text
          className={styles.listTitle}
          size='l'
          weight='semibold'
          variant='neutralCool'
          variantCount='900'
        >
          <FormattedMessage
            id='ReverseMortgage.listTitle'
            defaultMessage='A few things to know:'
          />
        </Text>
        <List.Bulleted className={styles.bulletList}>
          <FormattedMessage
            id='ReverseMortgage.bullet1'
            defaultMessage='If you want to stay in your home for the rest of your life, this may be a good solution for you'
          />

          <FormattedMessage
            id='ReverseMortgage.bullet2'
            defaultMessage='You have options to use your equity. Choose a line of credit, regular monthly payments, or a lump sum'
          />

          <FormattedMessage
            id='ReverseMortgage.bullet3'
            defaultMessage='The mortgage must be paid off when you sell your home, pass away, or move out'
          />
        </List.Bulleted>

        <div className={styles.otherOptions}>
          <Text
            className={styles.otherOptionsTitle}
            size='l'
            variant='neutralCool'
            variantCount='900'
          >
            <FormattedMessage
              id='ReverseMortgage.otherOptionsTitle'
              defaultMessage='Not sure about a reverse mortgage? You could consider:'
            />
          </Text>
          <div className={styles.row}>
            <DownsizingSvg />

            <div className={styles.rowContent}>
              <Text variant='neutralCool' variantCount='900'>
                <FormattedMessage
                  id='ReverseMortgage.downsizing.title'
                  defaultMessage='Downsizing'
                />
              </Text>

              <Text variant='neutralCool' variantCount='700'>
                <FormattedMessage
                  id='ReverseMortgage.downsizing.body'
                  defaultMessage='Buy a different home with a lower mortgage payment'
                />
              </Text>
            </div>
          </div>

          <div className={styles.row}>
            <LoanSvg />

            <div className={styles.rowContent}>
              <Text variant='neutralCool' variantCount='900'>
                <FormattedMessage
                  id='ReverseMortgage.cashout.title'
                  defaultMessage='A Cashout refinance'
                />
              </Text>

              <Text variant='neutralCool' variantCount='700'>
                <FormattedMessage
                  id='ReverseMortgage.cashout.body'
                  defaultMessage='Borrow against your current equity amount'
                />
              </Text>
            </div>
          </div>

          <div className={styles.row}>
            <HelocSvg />

            <div className={styles.rowContent}>
              <Text variant='neutralCool' variantCount='900'>
                <FormattedMessage
                  id='ReverseMortgage.heloc.title'
                  defaultMessage='A HELOC'
                />
              </Text>

              <Text variant='neutralCool' variantCount='700'>
                <FormattedMessage
                  id='ReverseMortgage.heloc.body'
                  defaultMessage='Get a line of credit using your current equity'
                />
              </Text>
            </div>
          </div>
          <OutreachButton
            handleClick={handleClick}
            success={dmSuccess}
            failure={dmFailure}
            requested={dmRequest}
            buttonMessage={buttonMessages.buttonText}
            resultMessage={
              dmSuccess
                ? intl.formatMessage(buttonMessages.buttonSuccess)
                : intl.formatMessage(buttonMessages.buttonFailure)
            }
          />
        </div>
      </Page.Body>
      <Text variant='neutralCool' variantCount='700' size='xs' element='i'>
        <FormattedMessage
          id='reverseMortgage.disclaimer'
          defaultMessage='Keep in mind that you will still be responsible for taxes, insurance, and other costs of homeownership.'
        />
      </Text>
      <Page.Footer showDisclaimer>
        <OverlayQuestionsFooter
          topic={intl.formatMessage(QUESTIONS.reverseMortgagesTopic)}
          topicKey={REVERSE_MORTGAGE_TOPIC_KEY}
          questions={Object.values(questionMessages)}
          placeholder
        />
      </Page.Footer>
    </Page>
  )
}

export default ReverseMortgagePanel
